import { SIGNUP_CREDITS, SUPPORT_EMAIL } from './landingData';

/**
 * Copy for /terms, /privacy and /data-deletion. A block is a paragraph (string) or a bullet
 * list (string[]). Bump LEGAL_UPDATED whenever either document changes.
 * Draft wording: have it reviewed by legal before treating it as final.
 */
export type LegalBlock = string | string[];
export interface LegalSection { id: string; title: string; blocks: LegalBlock[] }
export interface LegalDoc {
    eyebrow: string;
    title: string;
    titleEm: string;
    intro: string;
    seoDescription: string;
    sections: LegalSection[];
}

export const LEGAL_UPDATED = '24 October 2026';
export const LEGAL_ENTITY = 'PT Quality Agent Technologies';

export const TERMS: LegalDoc = {
    eyebrow: 'Terms of Service',
    title: 'The terms for',
    titleEm: 'using HyVay',
    intro: 'These terms set out what you can expect from HyVay and what we expect from you when you build, publish and share with it. Please read them before you create an account.',
    seoDescription: 'The terms that apply when you use HyVay to build, publish and share websites, applications and videos.',
    sections: [
        {
            id: 'agreement',
            title: 'About these terms',
            blocks: [
                `HyVay is operated by ${LEGAL_ENTITY} ("HyVay", "we", "us"). These Terms of Service apply to hyvay.com, HyVay Studio, the video maker, published sites on hyvay.com subdomains or connected domains, and any related service we provide (together, the "Service").`,
                'By creating an account or using the Service you agree to these terms and to our Privacy Policy. If you use HyVay on behalf of a company or organisation, you confirm that you are authorised to accept these terms for it.',
            ],
        },
        {
            id: 'account',
            title: 'Your account',
            blocks: [
                'You need an account to build or publish with HyVay. You can sign up with an email and password or with a supported sign-in provider.',
                [
                    'You must be at least 18 years old, or have permission from a parent or guardian.',
                    'Give accurate information and keep it up to date.',
                    'Keep your login secure. You are responsible for activity under your account.',
                    `Tell us at ${SUPPORT_EMAIL} straight away if you think someone else has accessed it.`,
                ],
            ],
        },
        {
            id: 'credits',
            title: 'Credits, payments and refunds',
            blocks: [
                `HyVay runs on credits. New accounts receive ${SIGNUP_CREDITS} free credits. Sending a message to the AI in Studio and publishing a site use credits at the rates shown on our Pricing page and in the app at the time you use them.`,
                [
                    'Credits are bought in rupiah through our payment partner. A platform fee is added to the subtotal and shown before you pay.',
                    'Credits do not expire while your account is active.',
                    'Credits have no cash value and cannot be transferred, sold or exchanged.',
                    'Purchased credits are non-refundable, including credits left unused when an account is closed.',
                    `If you were charged in error, for example a duplicate payment or credits that never reached your balance, contact ${SUPPORT_EMAIL} and we will correct it.`,
                ],
                'We may change credit prices or the credit cost of a feature. Changes apply from the time they are published and do not affect credits you already hold.',
            ],
        },
        {
            id: 'domains',
            title: 'Domains',
            blocks: [
                'Every published site gets a free hyvay.com subdomain. You can also connect a domain you already own, or buy a .com or .id domain inside HyVay.',
                [
                    'Domains are registered for the period shown at checkout and are subject to the rules of the relevant domain registry.',
                    'You must provide accurate registrant details. Some registries, such as .id, may ask for extra verification.',
                    'Domain purchases are non-refundable once registration has been completed.',
                    'Renewing the domain before it expires is your responsibility. An expired domain may be lost.',
                    'For a domain you connect yourself, you remain responsible for its registration and DNS settings.',
                ],
                'Subdomain names on hyvay.com are provided for as long as your site is published. We may reclaim names that are misleading, infringe someone else\'s rights or break these terms.',
            ],
        },
        {
            id: 'content',
            title: 'Your content and published sites',
            blocks: [
                'You keep ownership of what you bring to HyVay and what you build with it: your prompts, text, images, uploaded media, the generated code and your published sites ("Your Content").',
                'You give us a non-exclusive, worldwide licence to host, store, copy, process and display Your Content, only as needed to run, secure and improve the Service. This includes serving your published sites to the public. The licence ends when you delete the content or your account, apart from routine backups that are overwritten in time.',
                'Published sites can be viewed by anyone with the link. Do not publish anything you are not entitled to share, and do not put personal data or secrets you need to keep private on a public site.',
                'You are responsible for Your Content and for complying with laws that apply to your site, such as consumer protection, advertising, intellectual property and data protection rules.',
            ],
        },
        {
            id: 'ai',
            title: 'AI-generated output',
            blocks: [
                'HyVay uses AI to generate websites, copy, images, scripts, voiceovers and videos from your instructions. AI output can be inaccurate, incomplete or similar to output produced for other users.',
                [
                    'Review all output before you publish or rely on it.',
                    'Do not treat output as professional, legal, medical or financial advice.',
                    'Do not use the Service to create content that impersonates a real person or organisation without their permission.',
                ],
                'Subject to these terms, you may use the output you generate for any lawful purpose, including commercial use.',
            ],
        },
        {
            id: 'video-social',
            title: 'Video maker and connected social accounts (beta)',
            blocks: [
                'The video maker turns a brief, a link or your media into a short video. You can also connect your own social media accounts so HyVay can publish or schedule videos on them for you.',
                [
                    'By connecting an account you authorise HyVay to post the content you choose, at the time you choose, on that account.',
                    'You are responsible for what is posted and for following the rules of each social platform.',
                    'Only submit links and media you have the right to use. When you give us a link, we may visit it to capture text and images for your video.',
                    'You can disconnect an account at any time from HyVay. You can also revoke our access in the settings of the social platform itself.',
                ],
                'This feature is in beta. Scheduled posts can be delayed or fail, for example when a platform changes its rules or your connection expires. Check your accounts for anything time-critical.',
            ],
        },
        {
            id: 'acceptable-use',
            title: 'Acceptable use',
            blocks: [
                'Do not use HyVay to build, host or distribute anything that:',
                [
                    'is illegal in Indonesia or where you or your visitors are located;',
                    'is phishing, collects passwords or payment details under false pretences, or imitates another brand to mislead people;',
                    'contains malware, or attacks, probes or overloads any system, including HyVay\'s;',
                    'infringes copyright, trademarks or other rights of others;',
                    'is sexual content involving minors, promotes violence or terrorism, or harasses or discriminates against people;',
                    'is spam, gambling, or a scam or other fraud;',
                    'collects personal data from visitors without a lawful basis and a clear privacy notice.',
                ],
                'You also must not resell or sublicense access to the Service, get around credit limits or security controls, or scrape or reverse-engineer the Service except where the law allows it.',
            ],
        },
        {
            id: 'custom',
            title: 'Custom applications',
            blocks: [
                'When the HyVay team builds a custom application for you, the scope, price, timeline, ownership and support terms are set out in a separate quote or agreement. If that document conflicts with these terms, the separate document applies to that project.',
            ],
        },
        {
            id: 'beta',
            title: 'Beta and changing features',
            blocks: [
                'Features marked as beta are offered as they are, may change or be removed, and may not be as reliable as the rest of the Service. We work on HyVay continuously and may add, change or retire features. Where a change significantly reduces what you have paid for, we will tell you in advance where we reasonably can.',
            ],
        },
        {
            id: 'termination',
            title: 'Suspension and termination',
            blocks: [
                'You can stop using HyVay at any time and delete your account from your profile settings. Deleting your account removes your projects, unpublishes your sites and deletes your uploaded media. This cannot be undone.',
                'We may suspend or remove content, unpublish a site, or suspend or close an account if we reasonably believe it breaks these terms or the law, puts users or the Service at risk, or if we are required to by an authority. Where appropriate we will tell you why and give you a chance to respond, unless that would be unlawful or create a risk of harm.',
                'If we close your account for a serious or repeated breach, unused credits are forfeited.',
            ],
        },
        {
            id: 'disclaimer',
            title: 'Disclaimers',
            blocks: [
                'We work to keep HyVay available, secure and accurate, but the Service is provided "as is" and "as available". To the extent the law allows, we make no warranties that it will be uninterrupted, error-free or fit for a particular purpose, or that AI output will be accurate. Keep your own copies of anything important.',
            ],
        },
        {
            id: 'liability',
            title: 'Limitation of liability',
            blocks: [
                'To the extent the law allows, HyVay is not liable for indirect or consequential losses, such as lost profit, revenue, data or goodwill. Our total liability for any claim relating to the Service is limited to the amount you paid us in the 12 months before the event that gave rise to the claim.',
                'Nothing in these terms limits liability that cannot be limited under applicable law.',
            ],
        },
        {
            id: 'indemnity',
            title: 'Your responsibility to us',
            blocks: [
                'If someone brings a claim against HyVay because of Your Content or your breach of these terms, you agree to cover the reasonable losses and costs we incur as a result.',
            ],
        },
        {
            id: 'law',
            title: 'Governing law and disputes',
            blocks: [
                `These terms are governed by the laws of the Republic of Indonesia. If you have a complaint, please contact us first at ${SUPPORT_EMAIL} so we can try to resolve it. If it cannot be resolved amicably within 30 days, either party may take it to the competent courts in Indonesia.`,
            ],
        },
        {
            id: 'changes',
            title: 'Changes to these terms',
            blocks: [
                'We may update these terms as HyVay changes. The date at the top shows when they last changed. For significant changes we will notify you by email or in the app before they take effect. If you keep using HyVay after that, you accept the updated terms.',
            ],
        },
        {
            id: 'contact',
            title: 'Contact',
            blocks: [
                `Questions about these terms? Write to ${SUPPORT_EMAIL}.`,
            ],
        },
    ],
};

export const PRIVACY: LegalDoc = {
    eyebrow: 'Privacy Policy',
    title: 'How we handle',
    titleEm: 'your data',
    intro: 'This policy explains what personal data HyVay collects, why we collect it, who it is shared with and the choices you have. It is written to meet Indonesia\'s Personal Data Protection Law (UU PDP).',
    seoDescription: 'What personal data HyVay collects, how it is used and protected, and the rights you have over it.',
    sections: [
        {
            id: 'who',
            title: 'Who we are',
            blocks: [
                `HyVay is operated by ${LEGAL_ENTITY}. We are the data controller for the personal data described in this policy.`,
                'If you build a site or a custom application that collects data from your own visitors or customers, you are the controller of that data and we process it on your behalf. You are responsible for telling those people how their data is used.',
            ],
        },
        {
            id: 'collect',
            title: 'Data we collect',
            blocks: [
                'We collect only what we need to run HyVay:',
                [
                    'Account data: your name, email address, profile photo and how you sign in. If you sign in with email, we store your password only as a one-way hash.',
                    'Billing data: top-up orders, amounts, the platform fee and payment status. Card and bank details are entered with our payment partner, and HyVay does not store them.',
                    'Domain data: the registrant contact details needed to register a domain you buy through HyVay.',
                    'Content: your prompts and chat history in Studio, generated code, the sites you build and publish, and the images and files you upload.',
                    'Video data: briefs, links you submit, the text and images captured from those links, and the scripts, voiceovers and videos we generate for you.',
                    'Connected social accounts: the account name and identifier, the access permissions you grant, encrypted access tokens, and the posts you publish or schedule.',
                    'Technical data: IP address, browser and device information, and logs of requests and errors, which we keep for security and troubleshooting.',
                    'Messages you send us, such as support emails and custom application briefs.',
                ],
                'We do not intentionally collect specific (sensitive) personal data such as health, biometric or financial account data. Please do not put it in prompts or uploads.',
            ],
        },
        {
            id: 'use',
            title: 'How we use it',
            blocks: [
                [
                    'To create and secure your account and sign you in.',
                    'To build, preview, host and publish your sites and videos.',
                    'To process top-ups, keep a record of your credits, and register and manage domains.',
                    'To publish or schedule posts on the social accounts you connect, only when you ask us to.',
                    'To answer support requests and send service messages, such as receipts, security alerts and important changes.',
                    'To detect and prevent fraud, abuse and breaches of our Terms.',
                    'To understand how HyVay is used, so we can fix problems and improve it.',
                    'To meet our legal, tax and accounting obligations.',
                ],
                'We do not sell your personal data. We do not send marketing emails without your consent, and you can unsubscribe at any time.',
            ],
        },
        {
            id: 'basis',
            title: 'Legal basis',
            blocks: [
                'Under the UU PDP, we process your data on the following bases: to perform our agreement with you (providing the Service you signed up for), to meet legal obligations, for our legitimate interests (security, fraud prevention and improving HyVay, balanced against your rights), and on your consent where we ask for it, for example when you connect a social account. You can withdraw consent at any time. This does not affect processing done before you withdrew it.',
            ],
        },
        {
            id: 'ai',
            title: 'AI processing',
            blocks: [
                'To generate your site, copy, images, voiceovers or videos, we send your prompt and the relevant parts of your project to the AI models that power HyVay. We send only what is needed for the request, and we do not include your password or payment details.',
                'AI output is produced automatically. It does not lead to decisions that have legal or similarly significant effects on you.',
            ],
        },
        {
            id: 'share',
            title: 'Who we share it with',
            blocks: [
                'We share personal data only when it is needed:',
                [
                    'Service providers that help us run HyVay, such as hosting, payment processing, AI model, domain registration, image library and email delivery providers. They may use the data only to provide their service to us and must protect it.',
                    'Social platforms you connect, when you publish or schedule a post to them.',
                    'Domain registries, which may publish some registrant details according to their rules.',
                    'Authorities, when the law requires it or when needed to protect the rights, safety or property of our users, the public or HyVay.',
                    'A buyer or successor, if HyVay is involved in a merger or acquisition. Your data would remain protected under this policy.',
                ],
                'Anything you publish on a site or social account is public and can be seen by anyone.',
            ],
        },
        {
            id: 'transfer',
            title: 'Transfers outside Indonesia',
            blocks: [
                'Some of our service providers process data outside Indonesia. When they do, we make sure the destination offers an adequate level of protection, or we put appropriate safeguards in place, as required by the UU PDP.',
            ],
        },
        {
            id: 'retention',
            title: 'How long we keep it',
            blocks: [
                [
                    'Account data and content are kept while your account is active.',
                    'When you delete a project, it is removed from your account and unpublished. When you delete your account, your projects, published sites and uploaded media are deleted.',
                    'Disconnecting a social account deletes its stored access tokens.',
                    'Transaction and invoice records are kept for as long as tax and accounting laws require, even after you close your account.',
                    'Security logs are kept for a limited period and then deleted.',
                    'Backups are overwritten on a rolling basis, so deleted data may remain in a backup for a short time before it is gone.',
                ],
            ],
        },
        {
            id: 'security',
            title: 'How we protect it',
            blocks: [
                'We use technical and organisational measures that fit the risk. These include encryption in transit (HTTPS), hashed passwords, encryption at rest for social account tokens, access controls that limit staff access to what their role needs, and monitoring for abuse. No system is completely secure. If a data breach affects your personal data, we will notify you and the relevant authority as required by law.',
            ],
        },
        {
            id: 'rights',
            title: 'Your rights',
            blocks: [
                'Under the UU PDP you have the right to:',
                [
                    'get information about how your data is processed;',
                    'access and get a copy of your personal data;',
                    'correct inaccurate or incomplete data;',
                    'have your data deleted or its processing stopped;',
                    'withdraw consent you have given;',
                    'object to decisions made solely by automated processing;',
                    'restrict or suspend processing;',
                    'receive your data in a commonly used format and move it to another service.',
                ],
                `You can update your profile and delete your account yourself in your account settings. For anything else, email ${SUPPORT_EMAIL}. We may need to verify your identity, and we will respond within the time required by law. If you are not satisfied with our response, you may complain to the authority responsible for personal data protection in Indonesia.`,
            ],
        },
        {
            id: 'children',
            title: 'Children',
            blocks: [
                'HyVay is not intended for children. We do not knowingly collect personal data from anyone under 18 without the consent of a parent or guardian. If you believe a child has given us data, contact us and we will delete it.',
            ],
        },
        {
            id: 'cookies',
            title: 'Cookies and local storage',
            blocks: [
                'We use cookies and browser storage that are needed for HyVay to work: to keep you signed in, protect against misuse, and remember your preferences, such as light or dark theme. We do not use them for third-party advertising. Blocking them in your browser may stop parts of HyVay from working.',
            ],
        },
        {
            id: 'changes',
            title: 'Changes to this policy',
            blocks: [
                'We will update this policy when our practices change. The date at the top shows the latest version. For significant changes we will notify you by email or in the app before they take effect.',
            ],
        },
        {
            id: 'contact',
            title: 'Contact',
            blocks: [
                `For privacy questions or requests, write to ${SUPPORT_EMAIL} with "Privacy" in the subject line.`,
            ],
        },
    ],
};

/**
 * Data deletion instructions (Meta's "User data deletion" URL; also useful for the
 * other platform reviews). Describes only what HyVay actually does today: removing
 * the app on a platform revokes access but does not notify us, so it does not by
 * itself delete what HyVay stored.
 */
export const DATA_DELETION: LegalDoc = {
    eyebrow: 'Data Deletion',
    title: 'How to delete',
    titleEm: 'your data',
    intro: 'You can remove the data HyVay holds about you at any time, either for one connected social account or for your whole HyVay account. This page explains each way and what is deleted.',
    seoDescription: 'How to delete the data HyVay stores about you, including connected Facebook, Instagram, Threads, YouTube, TikTok and LinkedIn accounts.',
    sections: [
        {
            id: 'social',
            title: 'Disconnect a social account',
            blocks: [
                'Use this to remove one connected Facebook Page, Instagram, Threads, YouTube, TikTok or LinkedIn account.',
                [
                    'Sign in to HyVay and open Video maker, then "Jadwal posting & akun" (hyvay.com/video/schedule).',
                    'Under "Akun terhubung", click "Putuskan" next to the account and confirm.',
                ],
                'HyVay then deletes the stored connection to that account: its encrypted access tokens, the account name and picture, and the history of posts sent or scheduled to it. Scheduled posts to that account are cancelled.',
            ],
        },
        {
            id: 'account',
            title: 'Delete your HyVay account',
            blocks: [
                'Use this to remove your account and its content. Open Account settings, choose "Delete account" and confirm. Your projects, published sites, video projects and their rendered videos, uploaded media (including copies the video maker made from them) and all connected social accounts are deleted.',
                'Transaction and invoice records are kept for as long as tax and accounting laws require, as described in our Privacy Policy.',
            ],
        },
        {
            id: 'platform',
            title: 'Remove HyVay from a social platform',
            blocks: [
                'You can also revoke HyVay\'s access from the platform itself. HyVay can no longer post or read anything from that account afterwards.',
                [
                    'Facebook and Instagram: in Facebook, open Settings & privacy, then Settings, and go to Business integrations or Apps and websites. Select HyVay and remove it.',
                    'Threads: open Settings, then Account, then Website permissions, and remove HyVay.',
                    'YouTube (Google): remove HyVay at https://security.google.com/settings/security/permissions.',
                    'TikTok and LinkedIn: remove HyVay in the connected apps section of the platform\'s settings.',
                ],
                'Removing HyVay on the platform stops our access immediately. Threads tells us when you do this, and HyVay then deletes the stored connection to that Threads account automatically. The other platforms do not tell us, so to delete what we stored for them, disconnect the account in HyVay as described above, or ask us by email.',
            ],
        },
        {
            id: 'request',
            title: 'Ask us to delete your data',
            blocks: [
                `If you can no longer sign in, or want us to do it for you, email ${SUPPORT_EMAIL} with "Data deletion" in the subject line, from the email address of your HyVay account. We may need to verify your identity, and we will respond within the time required by law.`,
            ],
        },
        {
            id: 'scope',
            title: 'What is not affected',
            blocks: [
                [
                    'Posts already published on a social platform stay there. Delete them on that platform if you want them gone.',
                    'Backups are overwritten on a rolling basis, so deleted data may remain in a backup for a short time before it is gone.',
                ],
            ],
        },
    ],
};
